import toastr from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { generateURL } from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type {
  ResourceShareAttribute,
  SingleResourceShareResponse,
} from 'types/Share/ResourceShares';
import { useResourceTypes } from 'hooks/EntityTypes/useResourceTypes';

type MutationVariables = Partial<ResourceShareAttribute> & {
  id?: string;
};

export function useUpdateResourceShare(
  resourceShareID?: string | null | undefined
) {
  const queryClient = useQueryClient();
  const { keyByIDMap } = useResourceTypes();
  const updateResourceShare = (variables: MutationVariables) => {
    const { id: _id, ...rest } = variables;
    const id = _id || resourceShareID;

    if (!id) {
      return Promise.reject('Resource Share ID can not be null');
    }

    const url = generateURL(SPARK_RESOURCE_TYPES.RESOURCE_SHARES, [], id);

    const body = {
      data: {
        id: id,
        type: SPARK_RESOURCE_TYPES.RESOURCE_SHARES,
        attributes: rest,
      },
    };
    return fetchWrapper<SingleResourceShareResponse>({
      url: url,
      method: 'PATCH',
      body,
    });
  };

  return useMutation(
    [
      `updateResourceShare-${SPARK_RESOURCE_TYPES.RESOURCE_SHARES}`,
      resourceShareID,
    ],
    updateResourceShare,
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(
            `Failed to Update resource share record: ${error.message}`
          );
        }
      },
      onSuccess: (data) => {
        const { resourceID, resourceTypeID } = data.data.attributes;

        queryClient.invalidateQueries([
          SPARK_RESOURCE_TYPES.RESOURCE_SHARES,
          resourceID,
          resourceTypeID,
        ]);
        queryClient.invalidateQueries(keyByIDMap?.[resourceTypeID]);
      },
    }
  );
}
